import { useState, useEffect, useCallback } from 'react';
import type { GalleryResponse } from '@moltable/shared';
import { fetchGallery } from '../api/client';

type GallerySite = GalleryResponse['sites'][number];

interface UseGalleryResult {
  sites: GallerySite[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
}

export function useGallery(): UseGalleryResult {
  const [sites, setSites] = useState<GallerySite[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const loadGallery = useCallback(async (nextCursor?: string) => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchGallery(nextCursor);
      setSites(prev => (nextCursor ? [...prev, ...data.sites] : data.sites));
      setCursor(data.cursor);
      setHasMore(!!data.cursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load gallery');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGallery();
  }, [loadGallery]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore && cursor) {
      loadGallery(cursor);
    }
  }, [loading, hasMore, cursor, loadGallery]);

  return { sites, loading, error, hasMore, loadMore };
}
